import useParallelQueries from 'app/hooks/useParallelQueries';
import {
  IDocument,
  useDirectoryNodeFormMutation,
} from 'app/services/documents';
import SendButton from 'Components/Buttons/SendButton';
import UploadDocumentButton from 'Components/Buttons/UploadDocumentButton';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import TextArea from './TextArea';
import Uploader, { AttachmentErrors, IAttachment } from './Uploader';
import DocumentPillsList from './Uploader/DocumentPillsList';

interface MessageEditorProps {
  isSubmitting: boolean;
  disabled?: boolean;
  onSubmit: (content: string, documents: IDocument[], text: string) => void;
  canSubmit?: () => boolean;
}

const _MessageEditor = (
  { onSubmit, isSubmitting, disabled = false, canSubmit }: MessageEditorProps,
  ref: any,
) => {
  const [state, setState] = useState<'editing' | 'sending'>('editing');
  const [content, setContent] = useState('');
  const [attachmentErrors, setAttachmentErrors] = useState<AttachmentErrors>();
  const textAreaRef = useRef<any>();

  const {
    beginAll: uploadAll,
    results,
    register: registerParallelQueryHandle,
    unregister: unregisterParallelQueryHandle,
    isAllSuccess,
    reset: resetUpload,
  } = useParallelQueries<IDocument>();

  useImperativeHandle(ref, () => ({
    setEditState: () => setState('editing'),
  }));

  const prepareMessage = () => {
    if (canSubmit === undefined || (canSubmit && canSubmit())) {
      if (disabled || !!attachmentErrors || !content) return;
      uploadAll();
      setState('sending');
    }
  };

  const resetEditor = () => {
    setContent('');
    resetUpload();
    setState('editing');
  };

  const messageIsPreparingAndWaitingForSubmission = () => {
    const canSubmit =
      !!content && (!results || isAllSuccess) && state === 'sending';

    if (canSubmit) {
      const documents = Object.values(results || {})
        .filter((_) => _.data)
        .map((_) => _.data!);

      onSubmit(content, documents, textAreaRef?.current?.getRawContent());
    }
  };
  useEffect(messageIsPreparingAndWaitingForSubmission, [
    state,
    content,
    isAllSuccess,
  ]);

  const submissionRequestIsSentAndWaitingToResetEditor = () => {
    if (!isSubmitting) {
      resetEditor();
    }
  };
  useEffect(submissionRequestIsSentAndWaitingToResetEditor, [isSubmitting]);

  let [uploadDocument] = useDirectoryNodeFormMutation();

  const uploadDocumentHandle = (
    attachment: IAttachment,
  ): (() => Promise<IDocument>) => {
    return () =>
      uploadDocument({
        title: attachment.staged.name,
        file: attachment.staged,
        doctype: 'Other',
        description: '',
        object_type: 'document',
      }).unwrap();
  };

  return (
    <div className="px-8 max-w-full md-max:px-1 pt-4 pb-2 shadow-top-md">
      <TextArea
        ref={textAreaRef}
        value={content}
        onChange={setContent}
        onSubmit={prepareMessage}
        readOnly={disabled || isSubmitting || state === 'sending'}
      />
      <div className="p-2 bg-gray-light bg-opacity-20 flex justify-between rounded-b items-start">
        <Uploader
          state={state === 'editing' ? 'staging' : 'uploading'}
          onNewAttachment={(attachment) =>
            registerParallelQueryHandle(
              attachment.id,
              uploadDocumentHandle(attachment),
            )
          }
          onValidatiyChange={setAttachmentErrors}
        >
          {({
            attachments,
            deleteAttachment,
            addAttachements,
            state,
            errors,
          }) => (
            <>
              <div className="flex flex-1">
                {!!results && !!attachments && (
                  <div className="flex flex-col w-full">
                    <DocumentPillsList
                      attachmentQueryStatus={(attachment) =>
                        results[attachment.id]?.status
                      }
                      attachments={attachments}
                      onDelete={(attachment) => {
                        deleteAttachment(attachment);
                        unregisterParallelQueryHandle(attachment.id);
                      }}
                      className="mx-2"
                    />
                    {errors?.maxFilesCount && (
                      <div className="pt-2 text-red-dark text-xs">
                        {errors?.maxFilesCount}
                      </div>
                    )}
                  </div>
                )}
                <div className="ml-auto py-2">
                  <UploadDocumentButton
                    onChange={addAttachements}
                    disabled={state !== 'staging'}
                  />
                </div>
              </div>
            </>
          )}
        </Uploader>
        <div className="py-2">
          <SendButton
            onCancel={resetEditor}
            onClick={prepareMessage}
            isLoading={isSubmitting || state === 'sending'}
            disabled={
              disabled ||
              isSubmitting ||
              !content ||
              !!attachmentErrors ||
              state === 'sending'
            }
          />
        </div>
      </div>
    </div>
  );
};

const MessageEditor = forwardRef(_MessageEditor);
export default MessageEditor;
